import React, { useRef, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Badge, Collapse } from "reactstrap";
import { useReactToPrint } from "react-to-print";
import { useTenant } from "../../hooks/useTenant";
import { formatCurrency } from "../../utils/format";
import PrintableReceipt from "./PrintableReceipt";

/**
 * HistoricalReceiptModal
 * Usado em perfiles de clientes para visualizar e imprimir um recibo avulso de uma venda anterior.
 * Focado na visualização rápida e impressão, diferente do sucesso de nova venda.
 */
const HistoricalReceiptModal = ({ isOpen, toggle, saleData, memberName }) => {
    const { tenantName, tenantLogo } = useTenant();
    const printRef = useRef();

    const [openSections, setOpenSections] = useState({
        items: true,
        payments: false,
        installments: false
    });

    const toggleSection = (section) => {
        setOpenSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const handlePrint = useReactToPrint({
        contentRef: printRef, // Support for ReactToPrint Hooks
        content: () => printRef.current,
        documentTitle: `Recibo_${saleData?.saleNumber || "Venda"}`
    });

    if (!saleData) return null;

    return (
        <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
            <ModalHeader toggle={toggle} className="border-0 pb-0">
                <div className="d-flex align-items-center gap-2">
                    <div
                        className="d-flex align-items-center justify-content-center rounded-circle"
                        style={{
                            width: "40px",
                            height: "40px",
                            backgroundColor: "rgba(52, 152, 219, 0.1)"
                        }}
                    >
                        <i className="mdi mdi-receipt text-primary font-size-22"></i>
                    </div>
                    <div>
                        <h5 className="mb-0 fw-bold text-primary">Detalhamento da Venda</h5>
                        <p className="text-muted font-size-12 mb-0">
                            Nº {saleData.saleNumber}
                        </p>
                    </div>
                </div>
            </ModalHeader>

            <ModalBody className="pt-2 px-4">
                {/* Dados do membro */}
                <div className="d-flex align-items-center mb-4 pb-3 border-bottom">
                    <i className="mdi mdi-account-circle text-muted font-size-32 me-3"></i>
                    <div>
                        <h6 className="fw-bold font-size-15 mb-0 d-flex align-items-center text-dark">
                            {memberName || saleData.memberName || "Aluno"}
                            {saleData.memberNumber && (
                                <span className="ms-2 font-size-12 text-muted fw-normal">ID: {saleData.memberNumber}</span>
                            )}
                        </h6>
                        {(saleData.consultantName || saleData.employeeName) && (
                            <div className="text-muted font-size-13 mt-1">
                                Consultor: <span className="fw-semibold text-dark">{saleData.consultantName || saleData.employeeName}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Itens */}
                {saleData.items && saleData.items.length > 0 && (
                    <div className="mb-2 border rounded">
                        <div 
                            className="d-flex justify-content-between align-items-center p-3 cursor-pointer bg-white"
                            onClick={() => toggleSection('items')}
                            style={{ cursor: 'pointer' }}
                        >
                            <span className="text-dark font-size-13 fw-bold text-uppercase d-flex align-items-center">
                                <i className="mdi mdi-cart-outline me-2 font-size-18 text-muted"></i> Itens da Venda
                            </span>
                            <i className={`mdi mdi-chevron-${openSections.items ? 'up' : 'down'} font-size-18 text-muted`}></i>
                        </div>
                        <Collapse isOpen={openSections.items}>
                            <div className="d-flex flex-column gap-2 px-3 pb-3 pt-1 border-top">
                                {saleData.items.map((item, idx) => (
                                    <div key={idx} className="d-flex justify-content-between align-items-center py-2 border-bottom border-light">
                                        <div>
                                            <span className="font-size-14 fw-medium text-dark">{item.name}</span>
                                            <span className="font-size-11 text-muted ms-2 bg-light px-2 py-1 rounded">
                                                {item.type === "membership" ? "Plano" : item.type === "product" ? "Produto" : "Serviço"}
                                            </span>
                                        </div>
                                        <span className="fw-bold font-size-14 text-dark">
                                            {formatCurrency(item.totalPrice || item.unitPrice)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </Collapse>
                    </div>
                )}

                {/* Pagamentos */}
                {saleData.payments && saleData.payments.length > 0 && (
                    <div className="mb-2 border rounded">
                        <div 
                            className="d-flex justify-content-between align-items-center p-3 cursor-pointer bg-white"
                            onClick={() => toggleSection('payments')}
                            style={{ cursor: 'pointer' }}
                        >
                            <span className="text-dark font-size-13 fw-bold text-uppercase d-flex align-items-center">
                                <i className="mdi mdi-cash-multiple me-2 font-size-18 text-muted"></i> Pagamentos Efetuados
                            </span>
                            <i className={`mdi mdi-chevron-${openSections.payments ? 'up' : 'down'} font-size-18 text-muted`}></i>
                        </div>
                        <Collapse isOpen={openSections.payments}>
                            <div className="d-flex flex-column gap-2 px-3 pb-3 pt-1 border-top">
                                {saleData.payments.map((p, idx) => (
                                    <div key={idx} className="d-flex justify-content-between align-items-center py-2 border-bottom border-light">
                                        <div className="d-flex align-items-center">
                                            <span className="font-size-13 text-dark fw-medium">
                                                {p.methodLabel || "Pagamento"}
                                                {p.brand && <span className="text-muted fw-normal ms-1">({p.brand})</span>}
                                            </span>
                                        </div>
                                        <span className="fw-bold text-dark font-size-14">
                                            {formatCurrency(p.value)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </Collapse>
                    </div>
                )}

                {/* Parcelas / Financiamento */}
                {saleData.installments && saleData.installments.length > 0 && (
                    <div className="mb-4 border rounded">
                        <div 
                            className="d-flex justify-content-between align-items-center p-3 cursor-pointer bg-white"
                            onClick={() => toggleSection('installments')}
                            style={{ cursor: 'pointer' }}
                        >
                            <span className="text-dark font-size-13 fw-bold text-uppercase d-flex align-items-center">
                                <i className="mdi mdi-calendar-clock me-2 font-size-18 text-muted"></i> Histórico de Parcelas
                            </span>
                            <i className={`mdi mdi-chevron-${openSections.installments ? 'up' : 'down'} font-size-18 text-muted`}></i>
                        </div>
                        <Collapse isOpen={openSections.installments}>
                            <div className="d-flex flex-column gap-2 px-3 pb-3 pt-1 border-top">
                                {saleData.installments.map((inst, idx) => {
                                    const statusColors = {
                                        PENDING: "warning",
                                        PAID: "success",
                                        OVERDUE: "danger",
                                        CANCELED: "secondary"
                                    };
                                    // ACQUIRER PENDING = cliente já pagou via cartão
                                    const colorScheme = (inst.status === "PENDING" && inst.sourceType === "ACQUIRER")
                                        ? "success"
                                        : (statusColors[inst.status] || "dark");
                                    return (
                                        <div key={idx} className="d-flex justify-content-between align-items-center py-2 border-bottom border-light">
                                            <div>
                                                <span className="font-size-13 fw-bold text-dark d-block">
                                                    Parcela {inst.currentInstallment || (idx + 1)}/{inst.totalInstallments || saleData.installments.length}
                                                </span>
                                                <span className="font-size-12 text-muted">
                                                    Vence em: {inst.dueDate ? inst.dueDate.split("-").reverse().join("/") : "-"}
                                                </span>
                                            </div>
                                            <div className="text-end">
                                                <span className="fw-bold font-size-14 d-block text-dark mb-1">
                                                    {formatCurrency(inst.netAmount || inst.grossAmount)}
                                                </span>
                                                <Badge color={`${colorScheme}-subtle`} className={`text-${colorScheme} border font-size-10 px-2 py-1`}>
                                                    {inst.status === "PAID" 
                                                        ? "PAGO" 
                                                        : inst.status === "OVERDUE" 
                                                            ? "VENCIDO" 
                                                            : inst.status === "CANCELED" 
                                                                ? "CANCELADO" 
                                                                : inst.sourceType === "ACQUIRER" 
                                                                    ? "PAGO" 
                                                                    : "PENDENTE"
                                                    }
                                                </Badge>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </Collapse>
                    </div>
                )}

                {/* Totais */}
                <div className="pt-2">
                    {saleData.discountTotal > 0 && (
                        <div className="d-flex justify-content-between mb-2 text-muted font-size-13">
                            <span>Desconto Aplicado</span>
                            <span>- {formatCurrency(saleData.discountTotal)}</span>
                        </div>
                    )}
                    <div className="d-flex justify-content-between mb-2 text-dark font-size-14 fw-medium">
                        <span>Subtotal da Venda</span>
                        <span>{formatCurrency(saleData.subtotal || (saleData.totalAmount + saleData.discountTotal))}</span>
                    </div>
                    {saleData.balance > 0 && (
                        <div className="d-flex justify-content-between mb-3 text-muted font-size-13 d-flex align-items-center">
                            <span>Saldo Restante a Pagar</span>
                            <span className="fw-medium text-dark">{formatCurrency(saleData.balance)}</span>
                        </div>
                    )}
                    <div className="border-top border-dark pt-3 mt-2 d-flex justify-content-between fw-bold font-size-16 text-dark">
                        <span className="text-uppercase">Total</span>
                        <span>{formatCurrency(saleData.totalAmount)}</span>
                    </div>
                </div>
            </ModalBody>

            <ModalFooter className="border-0 px-4 py-3 d-flex justify-content-end bg-light rounded-bottom mx-1 mb-1">
                <Button 
                    color="primary" 
                    className="d-flex align-items-center justify-content-center rounded-0 shadow-sm p-0 m-0 border-0"
                    style={{ width: "45px", height: "45px" }}
                    onClick={handlePrint}
                    title="Imprimir Recibo"
                >
                    <i className="mdi mdi-printer font-size-20"></i>
                </Button>
            </ModalFooter>

            {/* Recibo oculto para impressão */}
            <div style={{ display: "none" }}>
                <div ref={printRef}>
                    <PrintableReceipt
                        saleData={saleData}
                        memberName={memberName}
                        tenantName={tenantName}
                        tenantLogo={tenantLogo}
                    />
                </div>
            </div>
        </Modal>
    );
};

export default HistoricalReceiptModal;

